import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@apollo/client'
import { useHistory, useLocation } from 'react-router-dom'
import { upperFirstOnly } from 'utils/format'
import { MetricTypes } from 'utils/constants'
import { KEY_PERFORMANCE_METRICS_QUERY } from '../../KeyPerformanceMetricsQueries'
import { DataTable } from '../../../performance-report/components/data-table'
import { fuelGradeTitlesToIds } from 'utils/helpers'
import { dateToString } from 'utils/dateTime'
import {
  stationsVar,
  fuelGradesVar,
  timeResolutionVar,
  currentPeriodDateVar,
  splitByVar,
  metricVar
} from 'config/apollo/analytics-report/variables'
import { useReactiveVar } from '@apollo/client'
import { useFlags } from 'launchdarkly-react-client-sdk'
import classNames from 'classnames/bind'
import styles from './KeyPerformanceMetricsReport.module.scss'
import Spinner from 'react-bootstrap/Spinner'
import moment from 'moment'
import { useFuelGrades } from 'components/common/hooks/useFuelGrades'
import { useCompanyTimeZone } from 'components/common/hooks/useCompanyTimeZone'
import { useStationGroups } from 'components/analytics/context/StationGroupsContext'
import { createDateRangeFromNow } from 'utils/dateTime'
import { ExportButton } from '../export-button'
import { dashboardNameForTab } from 'config/posthog/events'
import { useFormatMetricValue } from 'components/common/hooks/useFormatMetricValue'
import { snakeToCamel } from 'utils/snakeToCamel'
import Pagination from 'components/common/pagination'

const cx = classNames.bind(styles)

const METRIC_CONFIG = {
  total_fuel_volume_sales: { translationKey: 'widgets.analytics.totalVolume', type: MetricTypes.VOLUME },
  retail_fuel_volume_sales_for_computing_margin: { translationKey: 'widgets.analytics.retailVolume', type: MetricTypes.VOLUME },
  bunkered_fuel_volume_sales: { translationKey: 'widgets.analytics.bunkeredVolume', type: MetricTypes.VOLUME },
  consumer_fuel_volume_sales: { translationKey: 'widgets.analytics.consumerVolume', type: MetricTypes.VOLUME },
  fuel_card_fuel_volume_sales: { translationKey: 'widgets.analytics.fuelCardVolume', type: MetricTypes.VOLUME },
  gross_margin: { translationKey: 'widgets.analytics.grossMargin', type: MetricTypes.MARGIN },
  net_margin: { translationKey: 'widgets.analytics.netMargin', type: MetricTypes.MARGIN },
  retail_gross_sales_ex_vat_for_computing_margin: { translationKey: 'widgets.analytics.grossSales', type: MetricTypes.PROFIT },
  gross_profit: { translationKey: 'widgets.analytics.grossProfit', type: MetricTypes.PROFIT },
  net_profit: { translationKey: 'widgets.analytics.netProfit', type: MetricTypes.PROFIT },
  business_profit: { translationKey: 'widgets.analytics.businessProfit', type: MetricTypes.PROFIT },
  bunkering_commissions: { translationKey: 'widgets.analytics.bunkeringComissions', type: MetricTypes.COST },
  cost_price: { translationKey: 'widgets.analytics.replacementCost', type: MetricTypes.COST },
  total_fees: { translationKey: 'widgets.analytics.totalFees', type: MetricTypes.CURRENCY },
  retail_blended_cost: { translationKey: 'widgets.analytics.blendedCostSold', type: MetricTypes.PROFIT },
  num_retail_transactions: { translationKey: 'widgets.analytics.retailTransactions', type: MetricTypes.NUMBER },
  avg_retail_fill_up: { translationKey: 'widgets.analytics.retailFillUp', type: MetricTypes.FILL_UP },
}

const METRIC_SELECTION_GROUPS = ['volume', 'margin', 'profit', 'cost']
const TOGGLE_METRIC_KEYS = ['num_retail_transactions', 'avg_retail_fill_up']

const getMetricSelections = (selectedMetrics) => {
  if (!selectedMetrics || Array.isArray(selectedMetrics)) return {}

  return selectedMetrics
}

const getSelectedMetricItems = (metricSelections) => ([
  ...METRIC_SELECTION_GROUPS.flatMap((group) => metricSelections[group] || []),
  ...TOGGLE_METRIC_KEYS.filter((metricKey) => metricSelections[metricKey] === true),
])

const KeyPerformanceMetricsReport = ({ activeTab, mainData }) => {
  const { t } = useTranslation(['translations'])
  const { companyTimeZone } = useCompanyTimeZone()
  const isEdgeDataPro = mainData?.me.company.edgeDataPro
  const { groups } = useStationGroups()
  const { formatMetricValue } = useFormatMetricValue()
  // CSV export is gated behind LaunchDarkly. Until the flag is on, the button is
  // disabled with a "coming soon" tooltip (undefined flag → falsy → disabled).
  const { enableKeyPerformanceMetricsExport } = useFlags()

  const location = useLocation()
  const history = useHistory()
  const pageParam = new URLSearchParams(location.search).get('page')
  const page = pageParam && !isNaN(pageParam) && pageParam > 0 ? parseInt(pageParam) : 1

  const handlePageChange = (newPage) => {
    const params = new URLSearchParams(location.search)
    params.set('page', newPage)
    history.push(`${location.pathname}?${params.toString()}`)
  }
  
  // Get reactive variables
  const stationsFilter = useReactiveVar(stationsVar)
  const { fuelGrades: allFuelGrades, fuelGradesLoading } = useFuelGrades(stationsFilter[activeTab], null, isEdgeDataPro, groups)
  const fuelGradesFilter = useReactiveVar(fuelGradesVar)
  const timeResolutionFilter = useReactiveVar(timeResolutionVar)
  const currentPeriodDateFilter = useReactiveVar(currentPeriodDateVar)
  const splitByFilter = useReactiveVar(splitByVar)
  const metricFilter = useReactiveVar(metricVar)

  const splitBy = splitByFilter[activeTab] || []
  const showStation = splitBy.includes('Station')
  const showFuelGrade = splitBy.includes('Fuel Grade')
  
  const selectedMetricItems = useMemo(
    () => getSelectedMetricItems(getMetricSelections(metricFilter[activeTab])),
    [metricFilter, activeTab]
  )

  // Pagination resets to page 1 whenever the filters change (and on initial mount). We derive
  // the effective page *during render* rather than only resetting it in an effect after the
  // query has fired. Otherwise, changing a filter while on page 2+ fires a wasted query for the
  // stale page number before the effect resets the page to 1.
  const filterFingerprint = JSON.stringify([
    stationsFilter[activeTab],
    fuelGradesFilter[activeTab],
    timeResolutionFilter[activeTab],
    currentPeriodDateFilter[activeTab],
    splitByFilter[activeTab],
    metricFilter[activeTab],
  ])

  // null on the first render so initial mount is treated as a filter change → page 1.
  const committedFingerprintRef = useRef(null)
  const filtersChanged = committedFingerprintRef.current !== filterFingerprint
  const effectivePage = filtersChanged ? 1 : page

  useEffect(() => {
    committedFingerprintRef.current = filterFingerprint
    const params = new URLSearchParams(location.search)
    if (params.get('page') === '1') return
    params.set('page', '1')
    history.replace(`${location.pathname}?${params.toString()}`)
  }, [filterFingerprint])

  // Remove page query param from the URL when Key Performance Metrics Report unmounts (i.e. when switching to a different tab),
  // so tabs that don't use pagination don't display a stale page param and create confusion.
  useEffect(() => {
    return () => {
      const params = new URLSearchParams(history.location.search)
      if (!params.has('page')) return
      params.delete('page')
      const queryString = params.toString()
      history.replace(`${history.location.pathname}${queryString ? `?${queryString}` : ''}`)
    }
  }, [])

  // Execute query
  const {
    data: keyPerformanceMetricsDataResponse,
    loading: loadingKeyPerformanceMetricsData,
  } = useQuery(KEY_PERFORMANCE_METRICS_QUERY, {
    variables: {
      stationIds: stationsFilter[activeTab] || [],
      fuelGradeIds: fuelGradeTitlesToIds(fuelGradesFilter[activeTab] || [], allFuelGrades),
      metricNames: selectedMetricItems,
      currentPeriodStartDate: dateToString(
        companyTimeZone,
        currentPeriodDateFilter[activeTab]?.from ||
          createDateRangeFromNow(-7, -1, companyTimeZone).from
      ),
      currentPeriodEndDate: dateToString(
        companyTimeZone,
        currentPeriodDateFilter[activeTab]?.to || createDateRangeFromNow(-7, -1, companyTimeZone).to
      ),
      timeResolution: timeResolutionFilter[activeTab]?.toLowerCase() || 'daily',
      groupByStation: showStation,
      groupByFuelGrade: showFuelGrade,
      page: effectivePage,
    },
    skip:
      !fuelGradesFilter[activeTab]?.length ||
      !stationsFilter[activeTab]?.length ||
      selectedMetricItems.length === 0 ||
      fuelGradesLoading,
  })

  // Size the table and pagination block to fill the space between where the table starts to the bottom of the viewport.
  // This has been implemented to ensure the table and pagination are always visible.
  const tableSectionRef = useRef(null)
  const [tableSectionMaxHeight, setTableSectionMaxHeight] = useState(null)

  const measureTableSection = useCallback(() => {
    const node = tableSectionRef.current
    if (!node) return
    const BOTTOM_GUTTER = 12 // Used to define how much space to leave between the end of the content and the bottom of the viewport

    const documentTop = node.getBoundingClientRect().top + window.scrollY
    const availableHeight = Math.max(window.innerHeight - documentTop - BOTTOM_GUTTER, 200)
    setTableSectionMaxHeight((prev) => (prev === availableHeight ? prev : availableHeight))
  }, [])

  // Measure synchronously before paint whenever the rendered content changes (spinner↔table,
  // a new page, or new filter results), so the table fills the viewport without a flash.
  useLayoutEffect(() => {
    measureTableSection()
  }, [measureTableSection, loadingKeyPerformanceMetricsData, keyPerformanceMetricsDataResponse])

  // Keep the height in sync with viewport resizes and any layout shift above the table
  useEffect(() => {
    window.addEventListener('resize', measureTableSection)

    let observer
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(measureTableSection)
      observer.observe(document.body)
    }

    return () => {
      window.removeEventListener('resize', measureTableSection)
      if (observer) observer.disconnect()
    }
  }, [measureTableSection])

  const allMetrics = useMemo(
    () => Object.fromEntries(
      Object.entries(METRIC_CONFIG).map(([key, { translationKey, type }]) => [
        key,
        { display: upperFirstOnly(t(translationKey)), type }
      ])
    ),
    [t]
  )

  // Build headers: Split by columns first, then selected metrics
  const keyPerformanceMetricsHeaders = useMemo(() => {
    const headers = []
    if (showStation) headers.push('Station')
    if (showFuelGrade) headers.push('Fuel Grade')

    // Add headers for selected metrics
    selectedMetricItems.forEach(metricItem => {
      if (allMetrics[metricItem]) {
        headers.push(allMetrics[metricItem].display)
      }
    })

    return headers
  }, [showStation, showFuelGrade, selectedMetricItems, allMetrics])

  // Transform backend data to DataTable format
  const keyPerformanceMetricsData = useMemo(() => {
    if (loadingKeyPerformanceMetricsData || !keyPerformanceMetricsDataResponse?.keyPerformanceMetrics) {
      return []
    }

    const backendData = keyPerformanceMetricsDataResponse.keyPerformanceMetrics
    const { dates, stationNames, fuelGradeNames, metrics } = backendData

    if (!dates || dates.length === 0) {
      return []
    }

    const dataArray = []

    // Station column data
    if (showStation && stationNames && Array.isArray(stationNames)) {
      const stationData = dates.map((date, index) => ({
        date: moment.utc(date).format('YYYY-MM-DD HH:mm:ss'),
        value: stationNames[index] || ''
      }))
      dataArray.push(stationData)
    }

    // Fuel Grade column data
    if (showFuelGrade && fuelGradeNames && Array.isArray(fuelGradeNames)) {
      const fuelGradeData = dates.map((date, index) => ({
        date: moment.utc(date).format('YYYY-MM-DD HH:mm:ss'),
        value: fuelGradeNames[index] || ''
      }))
      dataArray.push(fuelGradeData)
    }

    selectedMetricItems.forEach(metricItem => {
      if (allMetrics[metricItem]) {
        // Convert our snake_case metricItem to camelCase to match backend
        const camelCaseKey = snakeToCamel(metricItem)

        const metricValues = metrics?.[camelCaseKey]

        if (metricValues && Array.isArray(metricValues)) {
          const metricData = dates.map((date, index) => ({
            date: moment.utc(date).format('YYYY-MM-DD HH:mm:ss'),
            value: parseFloat(metricValues[index])
          }))
          dataArray.push(metricData)
        }
      }
    })

    return dataArray
  }, [loadingKeyPerformanceMetricsData, keyPerformanceMetricsDataResponse, showStation, showFuelGrade, selectedMetricItems, allMetrics])

  // Pick a date format for the table. Show the year only when the data isn't entirely within the current year
  // I.e. when viewing for a previous year or spanning multiple years
  const dateFormat = useMemo(() => {
    const responseDates = keyPerformanceMetricsDataResponse?.keyPerformanceMetrics?.dates ?? []
    const currentYear = moment().year()
    const allDatesInCurrentYear = responseDates.length > 0 &&
      responseDates.every(date => moment.utc(date).year() === currentYear)
    return allDatesInCurrentYear ? 'ddd Do MMM' : 'ddd Do MMM YYYY'
  }, [keyPerformanceMetricsDataResponse])

  // Define metric types for each column
  const keyPerformanceMetricsTypes = useMemo(() => {
    const types = []
    if (showStation) {
      types.push(MetricTypes.TEXT) // Station
    }
    if (showFuelGrade) {
      types.push(MetricTypes.TEXT) // Fuel Grade
    }

    // Add metric types for selected metrics
    selectedMetricItems.forEach(metricItem => {
      if (allMetrics[metricItem]) {
        types.push(allMetrics[metricItem].type)
      }
    })

    return types
  }, [showStation, showFuelGrade, selectedMetricItems, allMetrics])

  // Format data for CSV export
  const formatExportData = () => {
    if (!keyPerformanceMetricsDataResponse?.keyPerformanceMetrics) {
      return []
    }

    const backendData = keyPerformanceMetricsDataResponse.keyPerformanceMetrics
    const { dates, stationNames, fuelGradeNames, metrics } = backendData

    if (!dates || dates.length === 0) {
      return []
    }

    const formattedData = []

    for (let i = 0; i < dates.length; i++) {
      const rowData = {
        'Date': moment.utc(dates[i]).format('Do MMM yyyy'),
      }

      // Add station column if shown
      if (showStation && stationNames && Array.isArray(stationNames)) {
        rowData['Station'] = stationNames[i] || ''
      }

      // Add fuel grade column if shown
      if (showFuelGrade && fuelGradeNames && Array.isArray(fuelGradeNames)) {
        rowData['Fuel Grade'] = fuelGradeNames[i] || ''
      }

      // Add metric columns
      selectedMetricItems.forEach(metricItem => {
        if (allMetrics[metricItem]) {
          const camelCaseKey = snakeToCamel(metricItem)
          const metricValues = metrics?.[camelCaseKey] || metrics?.[metricItem]
          
          if (metricValues && Array.isArray(metricValues) && metricValues[i] !== null && metricValues[i] !== undefined) {
            const metricType = allMetrics[metricItem].type
            const value = parseFloat(metricValues[i])
            rowData[allMetrics[metricItem].display] = formatMetricValue(metricType, value, false)
          } else {
            rowData[allMetrics[metricItem].display] = ''
          }
        }
      })

      formattedData.push(rowData)
    }

    return formattedData
  }

  // Generate filename for export
  const generateFilename = () => {
    const from = moment.utc(currentPeriodDateFilter[activeTab]?.from).format('DD/MM/yyyy')
    const to = moment.utc(currentPeriodDateFilter[activeTab]?.to).format('DD/MM/yyyy')
    return `key_performance_metrics_${from}-${to}`
  }

  const renderEmptyState = (titleKey, messageKey) => (
    <div className={cx('empty-state')}>
      <div className={cx('empty-state-content')}>
        <h2>{upperFirstOnly(t(titleKey))}</h2>
        <p>{t(messageKey)}</p>
      </div>
    </div>
  )

  // On initial load, the query is skipped while upstream data (stations/fuel grades filter data) is still resolving.
  // During that gap, loading is false and data is undefined. Treat this as a loading state.
  const queryHasResponded = !!keyPerformanceMetricsDataResponse?.keyPerformanceMetrics
  const queryAwaitingUpstream =
    !queryHasResponded &&
    selectedMetricItems.length > 0 &&
    (fuelGradesLoading ||
      !stationsFilter[activeTab]?.length ||
      !fuelGradesFilter[activeTab]?.length)

  if (loadingKeyPerformanceMetricsData || queryAwaitingUpstream) {
    return (
      <div className={cx('pro-widget-spinner')}>
        <Spinner animation="grow" variant="primary" />
      </div>
    )
  }

  if (keyPerformanceMetricsHeaders.length === 0) {
    return renderEmptyState(
      'widgets.analytics.noFiltersSelected',
      'widgets.analytics.enableMoreFilters'
    )
  }

  if (queryHasResponded && keyPerformanceMetricsData?.length === 0) {
    return renderEmptyState(
      'widgets.analytics.noDataTitle',
      'widgets.analytics.noDataMessage'
    )
  }
  
  const stickyColumns = [{
    metric: 'Date',
    width: 170
  }]

  if (showStation) {
    stickyColumns.push({
      metric: 'Station',
      width: 250
    })
  }

  if (showFuelGrade) {
    stickyColumns.push({
      metric: 'Fuel Grade',
      width: 150
    })
  }

  const pageInfo = keyPerformanceMetricsDataResponse?.keyPerformanceMetrics?.pageInfo
  const totalPages = pageInfo?.totalPages ?? 1
  const currentPage = Math.min(Math.max(effectivePage, 1), Math.max(totalPages, 1))

  return (
    <>
      <div
        ref={tableSectionRef}
        className={cx('table-section')}
        style={tableSectionMaxHeight ? { maxHeight: tableSectionMaxHeight } : undefined}
      >
        <div className={cx('key-performance-metrics-report')}>
          <DataTable
            data={keyPerformanceMetricsData}
            metrics={keyPerformanceMetricsHeaders}
            metricType={MetricTypes.VOLUME}
            metricTypes={keyPerformanceMetricsTypes}
            previousColumn={false}
            analytics={true}
            hideFooter={true}
            stickyColumns={stickyColumns}
            alternatingRows={true}
            enableColors={false}
            highlightWeekend={false}
            nextDayDelimiter={true}
            activeTab={activeTab}
            dateFormat={dateFormat}
          />
        </div>

        <div className={cx('pagination-and-export-wrapper')}>
          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              maxPagesToDisplay={5}
              padding="0"
              margin="0"
            />
          )}

          {/* margin-left: auto keeps the export button pinned right even when pagination isn't rendered */}
          {keyPerformanceMetricsData.length > 0 && (
            <ExportButton
              data={formatExportData}
              filename={generateFilename()}
              buttonName={t('widgets.analytics.exportData')}
              disabled={!enableKeyPerformanceMetricsExport}
              disabledTooltip={t('widgets.analytics.exportComingSoon')}
              padding="0"
              margin="0 0 0 auto"
              reportName={dashboardNameForTab(activeTab)}
              period={currentPeriodDateFilter[activeTab]}
            />
          )}
        </div>
      </div>
    </>
  )
}

KeyPerformanceMetricsReport.propTypes = {
  activeTab: PropTypes.string.isRequired,
  mainData: PropTypes.shape({
    me: PropTypes.shape({
      company: PropTypes.shape({
        edgeDataPro: PropTypes.bool
      })
    })
  })
}

export { KeyPerformanceMetricsReport }
